import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { apiClient } from '@/lib/api';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Status = 'loading' | 'reset-form' | 'success' | 'error';

/**
 * Handles auth action links sent to our own domain (`polymyth.quest/auth/action`)
 * instead of Firebase's hosted `*.firebaseapp.com/__/auth/action` page.
 *
 * - `verifyEmail` (oobCode): `applyActionCode` on load.
 * - `resetPassword` (oobCode): validate the code, then show a new-password form
 *   and `confirmPasswordReset` on submit.
 * - `changeEmail` / `revertEmail` (our signed `token`, NOT a Firebase oobCode):
 *   POST to `apply-email-change`, which completes the change server-side via the
 *   Admin SDK so Firebase never sends its unbranded firebaseapp.com notice.
 */
export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') ?? '';
  const oobCode = searchParams.get('oobCode') ?? '';
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');

  // resetPassword form state
  const [resetEmail, setResetEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (mode === 'verifyEmail') {
          if (!oobCode) throw new Error('missing oobCode');
          await applyActionCode(auth, oobCode);
          // Refresh the signed-in user (if any) so the flag flips without a
          // re-login. Best-effort.
          await auth.currentUser?.reload().catch(() => {});
          if (!cancelled) setStatus('success');
        } else if (mode === 'changeEmail' || mode === 'revertEmail') {
          // Completes server-side (Admin SDK) via our signed token, so Firebase
          // sends no unbranded firebaseapp.com notice. `applyEmailChange` returns
          // a business result rather than throwing on a bad token.
          if (!token) throw new Error('missing token');
          const result = await apiClient.applyEmailChange(token);
          if (cancelled) return;
          if (result.ok) {
            await auth.currentUser?.reload().catch(() => {});
            setStatus('success');
          } else {
            setErrorMsg(
              result.error === 'email_in_use'
                ? 'That email is now in use by another account.'
                : 'This link is invalid or has already been used.'
            );
            setStatus('error');
          }
        } else if (mode === 'resetPassword') {
          // Validate the code and learn which account it's for, then show the
          // new-password form. `confirmPasswordReset` happens on submit.
          if (!oobCode) throw new Error('missing oobCode');
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) {
            setResetEmail(email);
            setStatus('reset-form');
          }
        } else if (!cancelled) {
          setErrorMsg('This link uses an action that is not supported here.');
          setStatus('error');
        }
      } catch {
        if (!cancelled) {
          setErrorMsg('This link is invalid or has already been used.');
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, token]);

  const handleResetSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setErrorMsg('Choose a password of at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Those passwords do not match.');
      return;
    }
    setErrorMsg('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
    } catch {
      setErrorMsg('Could not reset your password. The link may have expired. Request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  const successTitle =
    mode === 'resetPassword'
      ? 'Password reset'
      : mode === 'changeEmail'
        ? 'Email updated'
        : mode === 'revertEmail'
          ? 'Email restored'
          : 'Email confirmed';
  const successBody =
    mode === 'resetPassword'
      ? 'Your password is updated. Sign in with your new password.'
      : mode === 'changeEmail'
        ? 'Your sign-in email has been updated.'
        : mode === 'revertEmail'
          ? 'Your sign-in email has been restored. Consider changing your password.'
          : 'Your email address is confirmed. The portal knows you now.';
  const successHref = mode === 'resetPassword' || mode === 'revertEmail' ? '/login' : '/dashboard';
  const successCta =
    mode === 'resetPassword' || mode === 'revertEmail' ? 'Sign in' : 'Continue to Polymyth';

  return (
    <div className="mx-auto max-w-md px-6 py-20 min-h-[70vh] flex items-center justify-center">
      <div className="w-full text-center space-y-6 rounded-2xl border border-border/50 bg-background/60 backdrop-blur-md p-8 shadow-xl">
        {status === 'loading' && (
          <>
            <AnkhSpinner size="lg" />
            <p className="text-muted-foreground">One moment…</p>
          </>
        )}

        {status === 'reset-form' && (
          <form onSubmit={handleResetSubmit} className="space-y-5 text-left">
            <div className="text-center space-y-1">
              <h1 className="text-2xl font-bold text-foreground">Choose a new password</h1>
              <p className="text-sm text-muted-foreground">
                Resetting the password for {resetEmail || 'your account'}.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Saving…' : 'Reset password'}
            </Button>
          </form>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{successTitle}</h1>
            <p className="text-muted-foreground">{successBody}</p>
            <p className="text-sm text-muted-foreground">
              <Link to={successHref} className="text-primary hover:underline">
                {successCta}
              </Link>
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Couldn't complete that</h1>
            <p className="text-muted-foreground">{errorMsg}</p>
            <p className="text-sm text-muted-foreground">
              Need help?{' '}
              <a href="mailto:tavian@polymyth.quest" className="text-primary hover:underline">
                Contact us
              </a>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
